import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useHistory, matchPath } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import { UserType } from 'further-types/user';
import { ApiResponse } from 'further-types/api';
import { Api } from 'further-types/further-user';
import showRequestError from 'helpers/layout/showRequestError';
import {
  AUTH_GATE_CHOOSE_ACCOUNT,
  AUTH_GATE_COPY_PROFILE,
} from 'routes/CommonRoutes';
import {
  resolveFirmAccountResolution,
  FirmAccountResolution,
  FirmAccountResolutionKind,
} from './resolveFirmAccount';
import { useCurrentUserInfo } from './useCurrentUserInfo';

type FirmIds = {
  eisFirmId?: string;
  lpFirmId?: string;
};

type RefetchCurrentUserInfo = ReturnType<typeof useCurrentUserInfo>['refetch'];

type Input = {
  isSubdomain: boolean;
  pathname: string;
  isLoggedIn: boolean;
  userType: UserType | undefined;
  activeAccountIds?: Array<string>;
  firmIds?: FirmIds;
  refetchCurrentUserInfo: RefetchCurrentUserInfo;
};

export function useResolveFirmAccount(input: Input): {
  resolution: FirmAccountResolution;
  isResolving: boolean;
} {
  const history = useHistory();
  const lastHandledKeyRef = useRef<string | null>(null);

  const activeAccountIds = input.activeAccountIds ?? [];
  const activeAccountId = activeAccountIds[0];
  const isFirmScoped = !!input.firmIds?.eisFirmId || !!input.firmIds?.lpFirmId;

  const isOnAuthGateRoute =
    !!matchPath(input.pathname, {
      path: AUTH_GATE_CHOOSE_ACCOUNT,
      exact: true,
    }) ||
    !!matchPath(input.pathname, { path: AUTH_GATE_COPY_PROFILE, exact: true });

  const shouldCheckSubdomainMismatch =
    input.isSubdomain &&
    input.isLoggedIn &&
    input.userType === UserType.Investor &&
    activeAccountIds.length > 0 &&
    isFirmScoped &&
    !isOnAuthGateRoute;

  const accountsQuery = useQuery({
    queryKey: [
      'auth',
      'firm-account-resolution',
      input.firmIds?.eisFirmId ?? null,
      input.firmIds?.lpFirmId ?? null,
      activeAccountIds.join(',') || null,
    ],
    enabled: shouldCheckSubdomainMismatch,
    queryFn: async (): Promise<Api.ListAccountsToAccessResponse> => {
      const response = await axios.get<
        ApiResponse<Api.ListAccountsToAccessResponse>
      >(`/further-user/list-accounts-to-access`, {
        params: { ...(input.firmIds ?? {}), includeCurrent: true },
      });
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
    retry: (failureCount: number, error: Error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const resolution = useMemo<FirmAccountResolution>(() => {
    return resolveFirmAccountResolution({
      enabled: shouldCheckSubdomainMismatch,
      activeAccountIds,
      accounts: accountsQuery.data,
    });
  }, [shouldCheckSubdomainMismatch, activeAccountIds, accountsQuery.data]);

  const accessAccountMutation = useMutation({
    mutationFn: async (data: { accountId: string; userType: UserType }) => {
      if (!input.firmIds) {
        throw new Error('firmIds are required to switch account');
      }
      await axios.post<ApiResponse<Api.AccessAccountResponse>>(
        `/further-user/access-account`,
        {
          accountId: data.accountId,
          userType: data.userType,
          firmIds: input.firmIds,
        },
      );
    },
    onSuccess: async () => {
      await input.refetchCurrentUserInfo();
    },
    onError: (error: AxiosError<ApiResponse<Record<string, never>>>) => {
      const message = error.response?.data?.responseMsg;
      showRequestError(new Error(message ?? error.message));
    },
  });

  useEffect(() => {
    const key = JSON.stringify({
      kind: resolution.kind,
      eisFirmId: input.firmIds?.eisFirmId ?? null,
      lpFirmId: input.firmIds?.lpFirmId ?? null,
      activeAccountId: activeAccountId ?? null,
      accountId:
        resolution.kind === FirmAccountResolutionKind.AutoSwitch
          ? resolution.accountId
          : null,
    });

    if (lastHandledKeyRef.current === key) return;

    if (
      resolution.kind === FirmAccountResolutionKind.NeedsChooseAccount &&
      history.location.pathname !== AUTH_GATE_CHOOSE_ACCOUNT
    ) {
      lastHandledKeyRef.current = key;
      history.replace(AUTH_GATE_CHOOSE_ACCOUNT);
      return;
    }

    if (resolution.kind === FirmAccountResolutionKind.AutoSwitch) {
      lastHandledKeyRef.current = key;
      accessAccountMutation.mutate({
        accountId: resolution.accountId,
        userType: resolution.userType,
      });
    }
  }, [
    resolution,
    activeAccountId,
    history,
    input.firmIds?.eisFirmId,
    input.firmIds?.lpFirmId,
  ]);

  const isResolving =
    accountsQuery.isFetching ||
    accessAccountMutation.isPending ||
    resolution.kind === FirmAccountResolutionKind.NeedsChooseAccount;

  return {
    resolution,
    isResolving,
  };
}
